// What a loading screen says is happening: the steps of a view's load,
// each with its state, as pure functions of the state the view's store
// holds. A step is a read the runtime really runs (a lane's `Remote`, or a
// stretch of one read that says how far it is), so nothing here counts
// time or guesses:
//
// - `waiting`: not asked for yet; `active`: being read; `done`: read;
// - `held`: it failed and the runtime will try it again by itself (a rate
//   limit's end); `failed`: it failed and waits for the user.
//
// A step has `progress` only where a real fraction is known (files listed
// of the files changed, comments read of the threads' counts). `needed`
// steps are what the view waits on; the others go on after it opens, as
// they do without a loading screen.
import type { Failure } from '../api/failure';
import type { Section } from '../logic/sections';
import type { PrTab } from '../router';
import { pendingAt } from '../runtime/effects';
import { manual, type Remote, type Retry } from '../runtime/remote';
import type { DashState, Slot } from '../state/dashboard/program';
import { MAX_TIMELINE_PAGES, type Fraction, type PrProgress } from '../state/pr/fetch';
import { failureIn, serverData, type PrLoads } from '../state/pr/program';

export type { Fraction };

export type StepStatus = 'waiting' | 'active' | 'done' | 'held' | 'failed';

export interface Step {
  id: string;
  label: string;
  status: StepStatus;
  /** A count or a note beside the label ("52", "200 so far"); null for none. */
  detail: string | null;
  /** How far an active step is, where that is a known fraction. */
  progress: Fraction | null;
  /** Whether the view waits on it. */
  needed: boolean;
}

/** The step that stopped a load: why, and how it is tried again. */
export interface Stopped {
  step: string;
  /** What was loading, as `statusOf` names it ("this pull request"). */
  what: string;
  failure: Failure;
  retry: Retry;
}

export interface LoadView {
  /** What is loading: "empathic/pathbase #499". */
  title: string;
  steps: Step[];
  /** The needed step that failed, when one did. */
  stopped: Stopped | null;
  /**
   * For a load whose time left can be estimated (loading/eta.ts): which
   * kind of load it is, for the pace past loads of it kept, and the
   * requests the view still waits on (null: not known yet, before the
   * first answer sizes it). Absent where no estimate is given.
   */
  pace?: { kind: string; left: number | null };
}

const step = (id: string, label: string, status: StepStatus, over: Partial<Step> = {}): Step => ({ id, label, status, detail: null, progress: null, needed: true, ...over });

/** A failure's status: held while its retry by itself is pending. */
const failedAs = (retry: Retry): StepStatus => (pendingAt(retry) === null ? 'failed' : 'held');

/** A lane's status. What it shows counts as done, whatever its refresh is doing. */
function statusOf(r: Remote<unknown>): StepStatus {
  switch (r.tag) {
    case 'NotAsked':
      return 'waiting';
    case 'Loading':
      return 'active';
    case 'Failed':
      return failedAs(r.retry);
    case 'Has':
      return 'done';
  }
}

/** A fraction worth showing: a whole of something, not yet overrun. */
const fraction = (done: number | null, total: number | undefined): Fraction | null =>
  done !== null && total !== undefined && total > 0 && done <= total ? { done, total } : null;

const count = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/** The first needed step that failed, as what stopped the load. */
function stoppedAt(steps: Step[], failures: Record<string, { what: string; in: Remote<unknown> }>): Stopped | null {
  for (const s of steps) {
    if (!s.needed || (s.status !== 'failed' && s.status !== 'held')) continue;
    const f = failures[s.id];
    const found = f && failureIn(f.in);
    if (found) return { step: s.id, what: f.what, failure: found.reason, retry: found.retry };
  }
  return null;
}

export interface PrTarget {
  owner: string;
  repo: string;
  number: number;
}

export const prTitle = (at: PrTarget) => `${at.owner}/${at.repo} #${at.number}`;

/**
 * The pull request page's load, for the view of `tab`: the pull request's
 * own read in the stretches it says it is at (its first answer, which
 * every tab waits on, then the further pages of threads, timeline and
 * checks, which come in after the page opens); the changed files, which
 * the Files tab waits on; then the viewer's drafts and the stack, which no
 * tab does.
 */
export function prSteps(s: PrLoads, at: PrTarget, tab: PrTab): LoadView {
  const pr = s.server.pr;
  // A pull request on screen counts as read, unless it is its first answer only (the rest still being read, or stopped).
  const lane = pr && !s.prPartial ? 'done' : statusOf(s.pr);
  const p = s.prProgress;
  // Past its first answer the read says which stretch it is at. Before it, the first request is
  // under way, and it reads the first page of threads, timeline and checks with the pull request:
  // those are under way with it, not waiting.
  const order = ['threads', 'timeline', 'checks'] as const;
  const together = lane === 'active' && !p;
  const stretch = (name: (typeof order)[number]): StepStatus => {
    if (lane !== 'active') return lane === 'done' ? 'done' : 'waiting';
    if (!p) return 'active';
    const here = order.indexOf(p.reading);
    const mine = order.indexOf(name);
    return mine < here ? 'done' : mine === here ? 'active' : 'waiting';
  };
  const WITH = 'with the pull request';
  const first: StepStatus = lane === 'active' ? (p ? 'done' : 'active') : lane;
  const threads = stretch('threads');
  const timeline = stretch('timeline');
  const checks = stretch('checks');
  const threadsOf = fraction(p?.threads ?? null, p?.threadsTotal ?? undefined);
  const events = p?.timelineTotal != null ? Math.min(p.timelineTotal, (MAX_TIMELINE_PAGES + 1) * PAGE) : undefined;
  const eventsOf = fraction(p?.timeline ?? null, events);
  const head = pr?.commits?.nodes[0]?.commit.statusCheckRollup?.contexts;
  const steps: Step[] = [
    step('pr', 'Pull request', first),
    // The page opens on the pull request's first answer (its first threads, newest timeline page and first checks), the rest coming in after.
    step('threads', 'Review threads', threads, {
      needed: false,
      progress: threads === 'active' ? (p?.comments ? fraction(p.comments.done, p.comments.total) : threadsOf) : null,
      detail: together
        ? WITH
        : threads === 'active' && p
          ? p.comments
            ? `${p.comments.done} of ${count(p.comments.total, 'comment')}`
            : threadsOf
              ? `${threadsOf.done} of ${count(threadsOf.total, 'thread')}`
              : `${p.threads} so far`
          : pr
            ? String(s.server.threads.length)
            : null,
    }),
    step('timeline', 'Timeline', timeline, {
      needed: false,
      progress: timeline === 'active' ? eventsOf : null,
      detail: together
        ? WITH
        : timeline === 'active' && p
          ? eventsOf
            ? `${eventsOf.done} of ${count(eventsOf.total, 'event')}`
            : `${count(p.timeline, 'event')} so far`
          : pr
            ? String(s.server.timeline.length)
            : null,
    }),
    step('checks', 'Checks', checks, {
      needed: false,
      progress: checks === 'active' ? fraction(p?.checks?.done ?? null, p?.checks?.total) : null,
      detail: together
        ? WITH
        : checks === 'active' && p?.checks
          ? `${p.checks.done} of ${p.checks.total}`
          : pr && head
            ? String(head.totalCount ?? head.nodes.length)
            : null,
    }),
  ];
  const files = statusOf(s.files);
  const changed = pr?.changedFiles ?? p?.files ?? undefined;
  // A listing under way says how far it is from its first page on: none listed yet is 0 of them.
  const listed = fraction(files === 'active' ? (s.filesListed ?? 0) : null, changed);
  steps.push(
    step('files', 'Changed files', files, {
      needed: tab === 'files',
      progress: files === 'active' ? listed : null,
      detail:
        files === 'active' && listed
          ? `${listed.done} of ${listed.total}`
          : files === 'waiting' && lane !== 'waiting' && lane !== 'done'
            ? // Listed at the head the pull request's answer names, so after it.
              changed !== undefined
              ? `${changed} · after the pull request`
              : 'after the pull request'
            : changed !== undefined
              ? String(changed)
              : null,
    }),
  );
  // Drafts are the changelist server's: not read where it keeps nothing for the repository.
  if (serverData(s)) steps.push(step('drafts', 'Your drafts', statusOf(s.drafts), { needed: false }));
  steps.push(step('stack', 'Stack', statusOf(s.stack), { needed: false }));
  return {
    title: prTitle(at),
    steps,
    stopped: stoppedAt(steps, {
      pr: { what: 'this pull request', in: s.pr },
      files: { what: "this pull request's changed files", in: s.files },
    }),
    pace: { kind: tab === 'files' ? 'pr+files' : 'pr', left: requestsLeft(lane, p, files, tab === 'files' ? (changed ?? null) : 0, s.filesListed) },
  };
}

/** Items GitHub sends a page of: threads, timeline items, check contexts and files alike. */
const PAGE = 100;
const pages = (n: number) => Math.max(0, Math.ceil(n / PAGE));

/**
 * The requests the view still waits on, as far as is known: the pull
 * request's further pages (threads, their comments, the timeline, the
 * checks), then the files' pages (`changed` files, `listed` so far; 0 when
 * the view does not wait on them). Null before the first answer says how
 * big the pull request is. An estimate: a stretch whose size is not given
 * counts one request, and comment pages read side by side count each.
 */
export function requestsLeft(lane: StepStatus, p: PrProgress | null, files: StepStatus, changed: number | null, listed: number | null): number | null {
  let left = 0;
  if (lane === 'waiting' || (lane === 'active' && !p)) return null;
  if (lane === 'active' && p) {
    const at = (['threads', 'timeline', 'checks'] as const).indexOf(p.reading);
    if (at === 0) left += p.comments ? pages(p.comments.total - p.comments.done) : p.threadsTotal != null ? pages(p.threadsTotal - p.threads) : 1;
    if (at <= 1) {
      const events = p.timelineTotal != null ? Math.min(p.timelineTotal, (MAX_TIMELINE_PAGES + 1) * PAGE) : null;
      left += events !== null ? pages(events - p.timeline) : 1;
    }
    if (at === 2) left += p.checks ? pages(p.checks.total - p.checks.done) : 1;
  } else if (lane !== 'done') return null;
  if (files !== 'done' && changed !== 0) {
    if (changed === null) return null;
    left += Math.max(1, pages(changed - (files === 'active' ? (listed ?? 0) : 0)));
  }
  return left;
}

/** The Files view's load once the pull request is in: the pull request, and its changed files. */
export function filesSteps(s: PrLoads, at: PrTarget): LoadView {
  const all = prSteps(s, at, 'files');
  const keep = new Set(['pr', 'files']);
  return { ...all, steps: all.steps.filter((x) => keep.has(x.id)) };
}

function sectionStep(section: Section, slot: Slot | undefined, i: number, attn: DashState['attn']): Step {
  const id = `section ${i}`;
  if (!slot) return step(id, section.title, 'waiting');
  const shown = slot.shown;
  if (shown.tag === 'Has') return step(id, section.title, 'done', { detail: String(shown.value.rows.length) });
  if (shown.tag === 'Failed') return step(id, section.title, failedAs(shown.retry));
  const flow = slot.flow;
  if (flow.at === 'searching') return step(id, section.title, 'active', { detail: 'searching' });
  if (flow.at === 'attention') {
    // Its rows are in; whose turn each is on is worked out row by row.
    const done = flow.prs.filter((p) => attn.get(p.id) === 'done').length;
    const progress = fraction(done, flow.prs.length);
    return step(id, section.title, 'active', { progress, detail: progress ? `${done} of ${count(flow.prs.length, 'pull request')}` : null });
  }
  return step(id, section.title, 'waiting');
}

/** The dashboard's load: each section's search, then its rows' attention. */
export function dashboardSteps(s: DashState): LoadView {
  return { title: 'Your dashboard', steps: s.sections.map((section, i) => sectionStep(section, s.slots[i], i, s.attn)), stopped: null };
}

/** Whether the dashboard has nothing to show yet: no section has rows or a failure of its own to say. */
export function dashboardCold(s: DashState): boolean {
  return s.sections.length > 0 && s.sections.every((_, i) => {
    const tag = s.slots[i]?.shown.tag;
    return tag === undefined || tag === 'Loading' || tag === 'NotAsked';
  });
}

/** A view whose load is one read: `label`, under way, done, or stopped by `failure`. */
export function oneStep(title: string, label: string, state: 'loading' | 'ready' | { failure: Failure; what: string }): LoadView {
  const failed = typeof state === 'object';
  return {
    title,
    steps: [step('load', label, failed ? 'failed' : state === 'ready' ? 'done' : 'active')],
    stopped: failed ? { step: 'load', what: state.what, failure: state.failure, retry: manual } : null,
  };
}

/**
 * What a polite live region says of a load: its title and the step under
 * way. It changes when another step begins, not as one counts up, and is
 * null once the load stopped (the failure notice says that itself) or has
 * nothing under way.
 */
export function announcement(v: LoadView): string | null {
  if (v.stopped) return null;
  const active = v.steps.find((s) => s.status === 'active' && s.needed) ?? v.steps.find((s) => s.status === 'active');
  return active ? `Loading ${v.title}: ${active.label.toLowerCase()}…` : null;
}

/** What a view's strip says while it is open on part of its load. */
export interface Streaming {
  /** The steps still under way, waiting or stopped that the view counts with: threads, timeline, checks, and the files on the Files tab. */
  steps: Step[];
  /** Why the rest stopped, when it did: the view stays open on what it has. */
  stopped: Stopped | null;
}

/**
 * What a view open on part of its load (the pull request's first answer,
 * the files listed so far) is still waiting for, from its load's steps:
 * nothing (null) once everything it counts with is in. The pull request's
 * own first answer is behind it (the view is open on it), and the drafts
 * and the stack are not counted with.
 */
export function streaming(view: LoadView): Streaming | null {
  const steps = view.steps.filter((s) => s.id !== 'pr' && s.id !== 'drafts' && s.id !== 'stack' && (s.id !== 'files' || s.needed) && s.status !== 'done');
  return steps.length || view.stopped ? { steps, stopped: view.stopped } : null;
}
